import { ServerError } from "@apollo/client/errors";
import { type RetryLink } from "@apollo/client/link/retry";
import { isAbortError } from "./abortErrors";

export const RETRY_MAX_ATTEMPTS = 5;
export const OFFLINE_WAIT_MS = 30_000;

// 401 can succeed once the Firebase ID token refreshes; other 4xx answer the same way every time.
const RETRYABLE_CLIENT_STATUSES = new Set([401, 408, 429]);

export const isRetryableError = (error: unknown): boolean => {
    if (isAbortError(error)) {
        return false;
    }
    if (ServerError.is(error)) {
        return error.statusCode >= 500 || RETRYABLE_CLIENT_STATUSES.has(error.statusCode);
    }
    return true;
};

export const waitForOnline = (timeoutMs: number): Promise<boolean> =>
    new Promise((resolve) => {
        const onOnline = () => {
            clearTimeout(timer);
            resolve(true);
        };
        const timer = setTimeout(() => {
            window.removeEventListener("online", onOnline);
            resolve(false);
        }, timeoutMs);
        window.addEventListener("online", onOnline, { once: true });
    });

interface ConnectivityEnv {
    isOnline: () => boolean;
    waitForOnline: (timeoutMs: number) => Promise<boolean>;
}

const browserEnv: ConnectivityEnv = { isOnline: () => navigator.onLine, waitForOnline };

// Offline, an immediate retry only burns an attempt; wait for the connection instead.
export const shouldRetry = async (error: unknown, env: ConnectivityEnv = browserEnv): Promise<boolean> => {
    if (!isRetryableError(error)) {
        return false;
    }
    if (!env.isOnline()) {
        return env.waitForOnline(OFFLINE_WAIT_MS);
    }
    return true;
};

export const retryLinkOptions: RetryLink.Options = {
    delay: { initial: 300, max: 5_000, jitter: true },
    attempts: { max: RETRY_MAX_ATTEMPTS, retryIf: (error) => shouldRetry(error) },
};
